// Does the payer's number belong to the operator of the payment method they
// chose? Each method from CORE carries its own number pattern, so the check is
// that pattern and nothing else: no prefix list, no operator code.
//
// This reads PaymentMethod.submitFields / queryFields, the instrument-level
// surface configured-fields-ownership.md rule 6 names. It is read for
// validation only and is never drawn on screen.
import type { PaymentMethod } from "../models/payment";
import { phoneCountry } from "../constants/phoneCountries";
import { compilePattern, testsAny } from "./safePattern";

// The codes that can carry the payer's number, most specific first. No
// captured CPS response holds a populated method pattern, so the field is
// chosen by its semantic code; any other field (an OTP, a reference) is never
// taken, because testing a number against it would refuse every payer.
const NUMBER_FIELD_IDS = ["CUSPHONE", "CUSNMB", "SVCNMB"] as const;

export const methodNumberPattern = (
  method: PaymentMethod | null | undefined
): RegExp | null => {
  if (!method) return null;

  const fields = [...(method.submitFields ?? []), ...(method.queryFields ?? [])];

  for (const code of NUMBER_FIELD_IDS) {
    const field = fields.find(item => item.fieldId === code && !!item.regex);
    if (field) return compilePattern(field.regex);
  }

  return null;
};

// True when the number may go ahead: it matches, or there is nothing safe to
// test it against. The pattern may expect the national number or the
// international form, so all three spellings of the same digits are tried.
export const matchesMethod = (
  method: PaymentMethod | null | undefined,
  national: string,
  countryCode: string
): boolean => {
  const pattern = methodNumberPattern(method);
  if (!pattern) return true;

  const digits = national.replace(/\D/g, "");
  const dial = (phoneCountry(countryCode)?.dialCode ?? "").replace(/\D/g, "");
  const candidates = dial ? [digits, `${dial}${digits}`, `+${dial}${digits}`] : [digits];

  return testsAny(pattern, candidates);
};

// Letters whose spoken name starts with a vowel sound (eff, aitch, em, ...).
const VOWEL_SOUND_LETTERS = "AEFHILMNORSX";

// "a" or "an" before a method name in English. A name whose first word is in
// capitals is read letter by letter ("an MTN", "a UBA"); any other word goes
// by its first letter being a vowel, which misjudges words like "Uni" or
// "Hour" but has no such method name today.
export const indefiniteArticle = (name: string): "a" | "an" => {
  const word = name.trim().split(/\s+/)[0] ?? "";
  if (!word) return "a";

  const first = word[0].toUpperCase();
  const initialism = word.length > 1 && /[A-Z]/.test(word) && word === word.toUpperCase();
  const vowels = initialism ? VOWEL_SOUND_LETTERS : "AEIOU";

  return vowels.includes(first) ? "an" : "a";
};
